import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import type { KataId } from '../../data/catalog/types'
import { useI18n } from '../../i18n'
import { useUnsavedGuard } from '../../lib/session/unsavedGuard'
import {
  emptyNoteDocument,
  isNoteDocumentEmpty,
  noteDocumentsEqual,
  type NoteDocument,
} from '../../storage/notes/document'
import {
  getNote,
  isFavourite,
  saveNote,
  setFavourite,
} from '../../storage/repositories/personal'
import styles from './PersonalDataPanel.module.css'

const NoteEditor = lazy(async () => {
  const mod = await import('./NoteEditor')
  return { default: mod.NoteEditor }
})

type Props = {
  kataId: KataId
  techniqueId: string
  techniqueName: string
  onFavouriteChange?: (techniqueId: string, favourite: boolean) => void
  /** `fullscreen` fills the notes dialog for longer writing sessions. */
  variant?: 'inline' | 'fullscreen'
}

/**
 * Notes + favourite controls. Storage access stays in the repository layer.
 * Notes are BlockNote documents (JSON blocks) — never treated as HTML.
 */
export function PersonalDataPanel({
  kataId,
  techniqueId,
  techniqueName,
  onFavouriteChange,
  variant = 'inline',
}: Props) {
  const { t, locale } = useI18n()
  const [initialContent, setInitialContent] = useState<NoteDocument | null>(null)
  const [draft, setDraft] = useState<NoteDocument>(emptyNoteDocument())
  const [baseline, setBaseline] = useState<NoteDocument>(emptyNoteDocument())
  const [editorReady, setEditorReady] = useState(false)
  const [editorEpoch, setEditorEpoch] = useState(0)
  const [favourite, setFavouriteState] = useState(false)
  const [loading, setLoading] = useState(true)
  const [savingNote, setSavingNote] = useState(false)
  const [togglingFav, setTogglingFav] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [noteStatus, setNoteStatus] = useState<string | null>(null)
  const [baselineStored, setBaselineStored] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setNoteStatus(null)
    setEditorReady(false)
    setEditorEpoch(0)
    setInitialContent(null)
    ;(async () => {
      try {
        const [note, fav] = await Promise.all([
          getNote(kataId, techniqueId),
          isFavourite(kataId, techniqueId),
        ])
        if (cancelled) return
        const content = note?.content ?? emptyNoteDocument()
        setInitialContent(content)
        setDraft(content)
        setBaseline(content)
        setBaselineStored(Boolean(note))
        setFavouriteState(fav)
      } catch {
        if (cancelled) return
        setError(t('personal.loadError'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [kataId, techniqueId, t])

  const remountEditor = (content: NoteDocument) => {
    setInitialContent(content)
    setDraft(content)
    setBaseline(content)
    setEditorReady(false)
    setEditorEpoch((n) => n + 1)
  }

  const noteDirty = editorReady && !noteDocumentsEqual(draft, baseline)
  useUnsavedGuard(noteDirty)

  const handleEditorReady = useCallback((doc: NoteDocument) => {
    setDraft(doc)
    setBaseline(doc)
    setEditorReady(true)
  }, [])

  const handleEditorChange = useCallback((doc: NoteDocument) => {
    setDraft(doc)
    setNoteStatus(null)
  }, [])

  const handleSaveNote = async () => {
    setSavingNote(true)
    setError(null)
    setNoteStatus(null)
    try {
      const saved = await saveNote(kataId, techniqueId, draft)
      if (saved) {
        setDraft(saved.content)
        setBaseline(saved.content)
        setBaselineStored(true)
        setNoteStatus(t('personal.noteSaved'))
      } else {
        remountEditor(emptyNoteDocument())
        setBaselineStored(false)
        setNoteStatus(t('personal.noteCleared'))
      }
    } catch {
      setError(t('personal.noteSaveFailed'))
    } finally {
      setSavingNote(false)
    }
  }

  const handleDeleteNote = async () => {
    if (!baselineStored && isNoteDocumentEmpty(draft)) return
    if (!window.confirm(t('personal.noteDeleteConfirm', { name: techniqueName }))) return
    setSavingNote(true)
    setError(null)
    try {
      await saveNote(kataId, techniqueId, emptyNoteDocument())
      remountEditor(emptyNoteDocument())
      setBaselineStored(false)
      setNoteStatus(t('personal.noteDeleted'))
    } catch {
      setError(t('personal.noteDeleteFailed'))
    } finally {
      setSavingNote(false)
    }
  }

  const handleToggleFavourite = async () => {
    setTogglingFav(true)
    setError(null)
    const next = !favourite
    try {
      await setFavourite(kataId, techniqueId, next)
      setFavouriteState(next)
      onFavouriteChange?.(techniqueId, next)
    } catch {
      setError(t('personal.favouriteFailed'))
    } finally {
      setTogglingFav(false)
    }
  }

  const rootClass =
    variant === 'fullscreen' ? `${styles.root} ${styles.fullscreen}` : styles.root

  if (loading || !initialContent) {
    return (
      <div className={rootClass} role="status">
        {t('personal.loading')}
      </div>
    )
  }

  const noteLabelId = `note-label-${techniqueId}`

  return (
    <div className={rootClass}>
      <div className={styles.header}>
        {variant === 'fullscreen' ? (
          <p id={noteLabelId} className={styles.noteContext}>
            {t('personal.noteLabel', { name: techniqueName })}
          </p>
        ) : (
          <h3 className={styles.title}>{t('personal.title')}</h3>
        )}
        <button
          type="button"
          className={`btn btn-secondary ${styles.favBtn}`}
          aria-pressed={favourite}
          disabled={togglingFav}
          onClick={() => void handleToggleFavourite()}
        >
          {favourite ? t('personal.favouriteOn') : t('personal.favouriteOff')}
        </button>
      </div>

      <div className={`field ${styles.editorField}`}>
        {variant !== 'fullscreen' && (
          <label id={noteLabelId}>{t('personal.noteLabel', { name: techniqueName })}</label>
        )}
        <div
          className={styles.editorHost}
          role="group"
          aria-labelledby={noteLabelId}
        >
          <Suspense fallback={<p className="field-hint">{t('personal.loading')}</p>}>
            <NoteEditor
              key={`${kataId}::${techniqueId}::${editorEpoch}`}
              initialContent={initialContent}
              locale={locale}
              onReady={handleEditorReady}
              onChange={handleEditorChange}
              variant={variant}
            />
          </Suspense>
        </div>
        <span className="field-hint">{t('personal.noteHint')}</span>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={savingNote || !noteDirty}
          onClick={() => void handleSaveNote()}
        >
          {savingNote ? t('common.saving') : t('personal.saveNote')}
        </button>
        <button
          type="button"
          className="btn btn-danger"
          disabled={savingNote || (!baselineStored && isNoteDocumentEmpty(draft))}
          onClick={() => void handleDeleteNote()}
        >
          {t('personal.deleteNote')}
        </button>
        {noteDirty && (
          <span className="banner banner-warning" role="status">
            {t('personal.unsavedNote')}
          </span>
        )}
        {noteStatus && (
          <span className="banner banner-success" role="status">
            {noteStatus}
          </span>
        )}
      </div>

      {error && (
        <p className="banner banner-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
